const ANIMATION_DURATION_MS = 1000
const MINIMUM_HOLD_DURATION_MS = 1200
const ANIMATION_EASING = 'cubic-bezier(0.4, 0, 0.2, 1)'
// Failsafe: if the target page never signals ready (e.g. error page rendered
// instead), retract the overlay anyway so the user isn't stuck.
const PAGE_READY_TIMEOUT_MS = 60_000

export const useBrandSwitchAnimationStore = defineStore('brandSwitchAnimation', () => {
  const router = useRouter()
  const initialDataLoaded = ref(false)
  const animationStage = ref<'idle' | 'started' | 'finishing'>('idle')
  const brandLogo = ref<string>('')
  const brandColor = ref('#000000')
  const doAnimation = ref(false)
  let pageReadyTimeoutId: ReturnType<typeof setTimeout> | null = null

  watch(router.currentRoute, (to, from) => {
    doAnimation.value = false

    // If page reload, for example, don't trigger the animation.
    const changedPage = to.fullPath !== from.fullPath

    // NOTE: Only animate when switching to landing page, not when navigating between other pages.
    // For example, when clicking on an open order in the "Account > Open Orders" table, we don't want to trigger the animation.
    if (normalizeRouteName(to.name) === 'landing' && changedPage) {
      doAnimation.value = true
    }
  })

  const canAnimate = computed(() => {
    return initialDataLoaded.value && doAnimation.value
  })
  const animationDuration = computed(() => {
    return ANIMATION_DURATION_MS
  })
  const animationEasing = computed(() => {
    return ANIMATION_EASING
  })
  const minimumHoldDuration = computed(() => {
    return canAnimate.value ? MINIMUM_HOLD_DURATION_MS : 0
  })
  const isActive = computed(() => animationStage.value !== 'idle')

  async function start(params: { brandLogo: string; brandColor: string }) {
    if (!canAnimate.value) {
      return
    }

    brandLogo.value = params.brandLogo
    brandColor.value = params.brandColor

    await nextTick()

    animationStage.value = 'started'

    if (pageReadyTimeoutId !== null) {
      clearTimeout(pageReadyTimeoutId)
    }
    pageReadyTimeoutId = setTimeout(() => {
      if (animationStage.value === 'started') {
        console.warn('BrandSwitchAnimation: page-ready timeout, retracting overlay')
        finishAndReset()
      }
    }, PAGE_READY_TIMEOUT_MS)
  }

  /**
   * Called by the target page's onMounted once it is in the DOM.
   * Retracts the overlay to reveal the already-rendered new page.
   */
  function notifyPageReady() {
    if (animationStage.value !== 'started') {
      return
    }
    finishAndReset()
  }

  function finishAndReset() {
    if (pageReadyTimeoutId !== null) {
      clearTimeout(pageReadyTimeoutId)
      pageReadyTimeoutId = null
    }

    animationStage.value = 'finishing'

    // Wait for finish animation to complete before resetting to idle
    setTimeout(() => {
      animationStage.value = 'idle'
    }, ANIMATION_DURATION_MS)
  }

  function markInitialDataLoaded() {
    initialDataLoaded.value = true
  }

  return {
    animationDuration,
    animationEasing,
    minimumHoldDuration,
    animationStage,
    brandColor,
    brandLogo,
    isActive,
    start,
    notifyPageReady,
    markInitialDataLoaded,
  }
})
